import { useTranslation } from 'react-i18next';
import {
  PayableDueState,
  PayableSettlement,
  SupplierPaymentStatus,
  type PayableInvoiceSummary,
} from '@optiflow/types';
import { Badge } from '@/components/ui/Badge';

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'brand';

/**
 * The three things a payables row says about itself, kept apart on purpose.
 *
 * A supplier invoice carries a *document* status and, separately, a *settlement* state. Collapsing
 * them into one chip is how a voided invoice ends up labelled "paid": it owes nothing, but nobody
 * paid it. Every badge here also says what it means in words, so none of this depends on colour.
 */

const SETTLEMENT_TONES: Record<PayableSettlement, Tone> = {
  [PayableSettlement.UNPAID]: 'warning',
  [PayableSettlement.PARTIALLY_PAID]: 'brand',
  [PayableSettlement.PAID]: 'success',
};

const DUE_TONES: Record<PayableDueState, Tone> = {
  [PayableDueState.SETTLED]: 'neutral',
  [PayableDueState.UPCOMING]: 'neutral',
  [PayableDueState.DUE]: 'warning',
  [PayableDueState.OVERDUE]: 'danger',
};

const PAYMENT_TONES: Record<SupplierPaymentStatus, Tone> = {
  [SupplierPaymentStatus.POSTED]: 'success',
  [SupplierPaymentStatus.VOIDED]: 'neutral',
};

/** What the document itself is: posted, or withdrawn. Never a settlement claim. */
export function InvoiceDocumentBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  return (
    <Badge tone={status === 'VOIDED' ? 'neutral' : 'brand'}>
      {t(`payables.documentStatus.${status}`)}
    </Badge>
  );
}

/** How much of a posted invoice has been paid. A voided invoice gets nothing here at all. */
export function SettlementBadge({ settlement }: { settlement: PayableSettlement | null }) {
  const { t } = useTranslation();
  if (!settlement) return <span className="text-muted text-xs">{t('payables.noSettlement')}</span>;

  return (
    <Badge tone={SETTLEMENT_TONES[settlement] ?? 'neutral'}>
      {t(`payables.settlement.${settlement}`)}
    </Badge>
  );
}

/**
 * Whether anyone should be chasing this today.
 *
 * Due today is its own state and is not overdue — the server decides that from the tenant's own
 * business date, and the browser's clock never gets a say.
 */
export function DueStateBadge({ invoice }: { invoice: PayableInvoiceSummary }) {
  const { t } = useTranslation();

  const label =
    invoice.due === PayableDueState.OVERDUE && invoice.daysOverdue
      ? t('payables.due.overdueBy', { count: invoice.daysOverdue })
      : invoice.due === PayableDueState.UPCOMING && invoice.daysUntilDue
        ? t('payables.due.inDays', { count: invoice.daysUntilDue })
        : t(`payables.due.${invoice.due}`);

  return <Badge tone={DUE_TONES[invoice.due] ?? 'neutral'}>{label}</Badge>;
}

export function SupplierPaymentStatusBadge({ status }: { status: SupplierPaymentStatus }) {
  const { t } = useTranslation();
  return (
    <Badge tone={PAYMENT_TONES[status] ?? 'neutral'}>{t(`payables.paymentStatus.${status}`)}</Badge>
  );
}
